import { StoredPublication } from "@/stores/publication";
import { getDebugMode } from "./helperFunctions";
import { Zoom as CustomZoomSetting } from "../../modules/comm/types";
import { Ref } from "vue";

export type UserSettingCSSVariable =
  | "scroll"
  | "columnCount"
  | "theme"
  | "fontSize"
  | "fontFamily"
  | "fontOverride"
  | "advancedSettings"
  | "pageLayoutPDF"
  | "firstPageByItself"
  | "customZoomPDF"
  | "handTool"
  | "ttsVoice"
  | "ttsHighlight"
  | "ttsLang"
  | "ttsVoiceVolume"
  | "ttsVoiceSpeed"
  | "ttsVoicePitch"
  | "ttsAutoTurn";
export type UserSettingCSSAppearance = "day" | "sepia" | "night" | "default";

export interface UserSetting {
  name: UserSettingCSSVariable;
  label: string;
  default: string | number | boolean;
  value?: string | number | boolean;
  global?: boolean;
  options: {
    label: string;
    value: string | number | boolean;
    active?: boolean;
    icon?: string;
  }[];
}

/**
 * Global settings are used independently of the type of content
 */
export type GlobalSettings = {
  /**
   * The theme used
   */
  theme: UserSettingCSSAppearance;
  themeOverrides?: {
    bg?: string;
  };
  [DefaultSettingsFlag]?: true; // Flag to indicate default settings obj
};

export enum CustomZoom {
  Fit = "page-fit",
  Height = "page-height",
  Width = "page-width",
}

// see: https://github.com/mozilla/pdf.js/blob/550520193085788c1dbb1dac0d4565109fbbb49f/web/ui_utils.js#L65
export enum PdfJsSpreadMode {
  Single = 0,
  Split = 1,
  FirstPageItself = 2,
}

// see: https://github.com/mozilla/pdf.js/blob/550520193085788c1dbb1dac0d4565109fbbb49f/web/ui_utils.js#L57
export enum PdfJsScrollMode {
  Vertical = 0, // Default value.
  Horizontal = 1,
  // we don't use that one
  // WRAPPED = 2,
  PagedNoScroll = 3,
}

/**
 * Settings that are specific to a content
 */
export type PerContentSettings = {
  /** Number in range 0.7 - 2.5 (percentage representation) */
  fontSize?: number;
  fontFamily?: string;
  /**
   * Page Layout -- to simplify the API options, PDF will use only 3 options
   * 0: "auto"
   * 1: "Single"
   * 2: "Dual"
   * But for EPUB, we will allow any number of columns,
   * as some publications may require more than 2 columns (e.g. comics)
   */
  columnCount?: number;
  /**
   * The scroll mode
   */
  scroll?: boolean;
  /**
   * Page Layout for PDF
   * 1: "Auto"
   * 0: "Single"
   */
  pageLayoutPDF?: 1 | 0;
  firstPageByItself?: boolean;
  customZoomPDF?: CustomZoomSetting;
  /**
   * Whether drag-to-pan (hand tool) is enabled for PDF, as opposed to the
   * select/cursor tool used for text selection.
   */
  handTool?: boolean;
  fontOverride?: boolean;
  advancedSettings?: boolean;
  /**
   * HTML color for TTS highlight
   */
  ttsHighlight?: `#${string}`;
  ttsAutoTurn?: boolean;
  /**
   * Name of the voice used for TTS
   */
  ttsVoice?: string | null;
  ttsLang?: string;
  ttsVoiceSpeed?: number;
  ttsVoicePitch?: number;
  ttsVoiceVolume?: number;
  version?: number;
};

const defaultsPDF = {
  pageLayoutPDF: 1,
  customZoomPDF: "0",
  handTool: true,
};

const defaultsGeneral = {
  scroll: false,
  firstPageByItself: true,
  ttsHighlight: "#f0ba32",
  ttsAutoTurn: true,
  ttsVoice: null,
  ttsLang: null,
  ttsVoiceSpeed: 1,
  ttsVoicePitch: 1,
  ttsVoiceVolume: 50,
};

const defaultsEPUB = {
  advancedSettings: false,
  columnCount: 2,
  fontFamily: "Default",
  fontSize: 1,
  fontOverride: true,
};

export const DefaultSettingsFlag = Symbol("DefaultSettingsFlag");

export const defaultPerContentSettings = {
  "application/pdf": {
    ...defaultsPDF,
    ...defaultsGeneral,
    [DefaultSettingsFlag]: true, // Flag to indicate default settings obj
  },
  "application/xhtml+xml": {
    ...defaultsEPUB,
    ...defaultsGeneral,
    [DefaultSettingsFlag]: true, // Flag to indicate default settings obj
  },
  "application/xhtml+xml--FXL": {
    ...defaultsEPUB,
    ...defaultsGeneral,
    [DefaultSettingsFlag]: true, // Flag to indicate default settings obj
  },
};

const normalizeValue = (
  keyName: string,
  value: string
): string | boolean | number => {
  switch (keyName) {
    case "fontOverride":
      return value === "readium-advanced-on";
    case "fontSize":
      return parseFloat(value) / 100;
    case "columnCount": {
      /*
       * Here we need to validate if the value is 0 (as it was before in readiumCSS v1), and transform
       * it to 2 (as "default"), because there's not "default" column value in preferences API
       */
      const parsedValue = parseInt(value, 10);
      return parsedValue === 0 ? 2 : parsedValue;
    }
    case "scroll":
      return value === "true";
    case "pageLayoutPDF":
      return parseInt(value, 10);
    case "firstPageByItself":
      return value === "true";
    case "handTool":
      return value === "true";
    case "customZoomPDF":
      return value;
    case "ttsHighlight":
      return value;
    case "ttsAutoTurn":
      return value === "true";
    case "ttsVoiceSpeed":
      return parseFloat(value);
    case "ttsVoicePitch":
      return parseFloat(value);
    case "ttsVoiceVolume":
      return parseInt(value, 10);
    case "theme": {
      return value.split("-")[1];
    }
    default:
      return value;
  }
};

const renameStoreKeys = (storeSettings): void => {
  if (!storeSettings) return;

  if (storeSettings["version"] && storeSettings["version"] === 2.0) {
    return;
  }

  Object.keys(storeSettings).forEach((fullKey) => {
    if (fullKey.startsWith("--USER__")) {
      let key = fullKey.replace("--USER__", "");
      if (key === "colCount") key = "columnCount";
      if (key === "advancedSettings") key = "fontOverride";
      if (key === "appearance") key = "theme";

      const value = normalizeValue(key, storeSettings[fullKey]);
      storeSettings[key] = value;
      delete storeSettings[fullKey];
      if (getDebugMode())
        console.log(`Key ${fullKey} renamed to ${key} with value ${value}`);
    }
  });
  storeSettings["version"] = 2.0; // Set version to 2.0 as a sanity check after procesing keys
};

export const standardizePublicationStorageKeys = (
  storage: StoredPublication
): void => {
  if (storage.settings.version !== 2.0) {
    if (getDebugMode())
      console.log(
        "The publication is not entirely standardized, renaming keys..."
      );
    renameStoreKeys(storage.settings);
  } else {
    if (getDebugMode())
      console.log("Publication settings already standardized to version 2.0");
  }
};

export function standardizeGlobalStorageKeys(
  storage: Ref<GlobalSettings>
): void {
  const globalSettings = storage.value;
  if (!globalSettings) {
    console.error("No global settings found");
    return;
  }
  renameStoreKeys(globalSettings);

  // iterate over all link types
  Object.keys(globalSettings).forEach((linkType) => {
    if (linkType.startsWith("application/")) {
      renameStoreKeys(globalSettings[linkType]);
    }
  });
}
